import { CardReader } from '@cloudbeds/card-reader';

import { titleCase } from '../utils/capitalize';
import { createLogLocal } from '../utils/createLogLocal';

import type { CardData } from '@cloudbeds/card-reader';
import type { UseFormReturn } from 'react-hook-form';

const log = createLogLocal('CardReaderWrapper');

/**
 * Props for the CardReaderWrapper component.
 * Provides the dependencies needed to handle card reader data completion.
 */
export type CardReaderWrapperProps = {
  /**
   * React Hook Form methods for getting and setting form values.
   * Used to populate card fields when card reader data is received.
   */
  formMethods: Pick<UseFormReturn, 'getValues' | 'setValue'>;

  /**
   * Setter function to store the card number in a gateway-agnostic way.
   * For TokenEx: stores the card number for iframe injection (used by card readers).
   * For Stripe: no-op (Stripe Elements don't support programmatic injection).
   */
  setCardNumber: (cardNumber: string | null) => void;

  /**
   * Gateway-agnostic function to focus the secure card input element.
   * Used to focus the CVV field after card data is populated from a card reader.
   * For TokenEx: focuses the secure iframe.
   * For Stripe: focuses the Stripe Element (if supported).
   */
  focusSecureInput: () => void;
};

/**
 * Wrapper component for the CardReader that handles the onComplete callback.
 * Encapsulates the logic for populating form fields when card data is
 * received from a magnetic stripe card reader.
 *
 * This component:
 * - Populates cardholder name (if empty) from card reader data
 * - Populates expiry date (if empty) from card reader data
 * - Always populates the card number field
 * - Stores the card number in the card reader data store
 * - Focuses the CVV iframe field after a short delay
 *
 * @param props - The component props containing form methods and store accessors
 * @returns The CardReader component with the onComplete callback configured
 */
export const CardReaderWrapper = ({
  formMethods,
  setCardNumber,
  focusSecureInput,
}: CardReaderWrapperProps) => {
  /**
   * Handles the completion event from the CardReader component.
   * Populates form fields with data extracted from the card reader.
   *
   * @param cardReaderData - The parsed card data from the magnetic stripe reader
   */
  const handleCardReaderComplete = (cardReaderData: CardData) => {
    log('<CardReader> onComplete', 'data', cardReaderData);

    // If the cardholder name already has content, don't overwrite it;
    // same applies to expiry date.
    const { cardholderName, cardExpire } = formMethods.getValues();
    const {
      MM: rawExpiryMonth,
      YY: rawExpiryYear,
      firstName,
      lastName,
      cardNumber,
    } = cardReaderData;

    // Handle cardholder name
    if (!cardholderName) {
      const rawName = titleCase(
        lastName ? `${firstName} ${lastName}` : firstName,
      );
      formMethods.setValue('cardholderName', rawName);
    }

    // Handle expiry date
    if (!cardExpire) {
      const expiryFormatted = `${rawExpiryMonth}/${rawExpiryYear}`;
      formMethods.setValue('cardExpire', expiryFormatted);
    }

    // Handle card number
    formMethods.setValue('cardNumber', cardNumber);
    setCardNumber(cardNumber);

    // Timeout to prevent typing any other card data into the CVV field
    setTimeout(() => {
      focusSecureInput();
    }, 700);
  };

  return <CardReader onComplete={handleCardReaderComplete} />;
};
